import {catalogTypeOptions} from '@/modules/drug-catalog/constants'
import {isTypeActiveSubstance, isTypeSolution, isTypeSupportiveMedication} from '@/modules/drug-catalog/functions'
import {useMedicationProfileForm} from '@/modules/drug-catalog/hooks/use-medication-profile-form'
import {
  mapMedicationProfileFormToApiValues,
  mapMedicationProfilesToSelectItems,
} from '@/modules/drug-catalog/medication-profiles/functions'
import {useGetInfiniteSolutionDrugs} from '@/modules/drug-catalog/solutions/api'
import {useGetInfiniteActiveSubstanceDrugs} from '@/modules/drug-catalog/substances/api'
import {useGetInfiniteSupportiveMedicationDrugs} from '@/modules/drug-catalog/supportive-medications/api'
import {useGetPrescribedNames} from '@/modules/products/trade-names/api'
import {useSelectedDrugItem} from '@/modules/products/trade-names/components/trade-name-form/utils'
import {volumeUnitEndElement} from '@/modules/products/trade-names/components/trade-name-form/volume-unit'
import {containerContentsOptions} from '@/modules/products/trade-names/constants'
import {getTradeNameFormDefaultValues, mapPrescribedNamesToSelectItems} from '@/modules/products/trade-names/functions'
import {useGetUnits} from '@/modules/products/units/api'
import {mapUnitsToSelectItems} from '@/modules/products/units/functions'
import {useZero} from '@/shared/integrations/zero/use-zero'
import {Box, HStack, IconButton} from '@chakra-ui/react'
import {CatalogType} from '@cypro/engine/database/schema'
import {FormSection} from '@cypro/ui/components/form/form-section'
import {FormGrid} from '@cypro/ui/components/ui/form-grid'
import {Tooltip} from '@cypro/ui/components/ui/tooltip'
import {withForm} from '@cypro/ui/integrations/form'
import {useStore} from '@tanstack/react-form'
import {Link} from '@tanstack/react-router'
import {useTranslate} from '@tolgee/react'
import {LuPlus} from 'react-icons/lu'
import {uuidv7} from 'uuidv7'

export const TradeNameEssentialSetupTab = withForm({
  defaultValues: getTradeNameFormDefaultValues(undefined),
  render: function Render({form}) {
    const {t} = useTranslate('common')
    const {t: tTradeNames} = useTranslate('trade-names')

    const zero = useZero()

    const type = useStore(form.store, state => state.values.type)
    const drugCatalogItemId = useStore(form.store, state => state.values.drugCatalogItemId)
    const isDrySubstance = useStore(form.store, state => state.values.isDrySubstance)
    const concentrationUnitError = useStore(
      form.store,
      state => state.fieldMeta.concentrationUnitId?.errors?.[0]?.message
    )

    const catalogTypeSelectItems = catalogTypeOptions.map(item => ({label: t(item), value: item}))

    // only the selected substance's prescription trade names, so another drug's stock can't mix in (CY-368)
    const {prescribedNames} = useGetPrescribedNames({filters: {drugCatalogItemId: drugCatalogItemId || undefined}})
    const prescribedNameItems = drugCatalogItemId ? mapPrescribedNamesToSelectItems(prescribedNames || []) : []
    const isActiveSubstance = isTypeActiveSubstance(type)
    const isSupportiveMedication = isTypeSupportiveMedication(type)
    const isSolution = isTypeSolution(type)

    const {units: strengthUnits} = useGetUnits({filters: {type: 'mass'}})
    const {units: concentrationUnits} = useGetUnits({filters: {type: 'concentration'}})
    const strengthUnitsSelectItems = mapUnitsToSelectItems(strengthUnits)
    const concentrationUnitsSelectItems = mapUnitsToSelectItems(concentrationUnits)

    const contentsSelectItems = containerContentsOptions.map(item => ({label: t(item), value: item}))

    const [drug] = useSelectedDrugItem(type, drugCatalogItemId)
    const medicationProfileSelectItems = mapMedicationProfilesToSelectItems(drug?.medicationProfiles)

    const {onAddMedicationProfile} = useMedicationProfileForm({
      onSubmit: async value => {
        if (!drug) return

        // generate the id up front so the trade name can select the profile as soon as the optimistic client mutation lands
        const id = uuidv7()

        await zero.attemptMutation(
          zero.mutators.medicationProfiles.add({
            ...mapMedicationProfileFormToApiValues(value),
            id,
            drugCatalogItemId: drug.id,
          }),
          async mutation => {
            await mutation.client
            form.setFieldValue('medicationProfileId', id)
            form.setFieldValue('isDrySubstance', Boolean(value.isDrySubstance))
          }
        )
      },
    })

    const additionalsUsed = useStore(form.store, state => {
      if (state.values.type === CatalogType.enum.ACTIVE_SUBSTANCE) return state.values.additionalsUsed
      return null
    })

    return (
      <Box spaceY="8">
        <FormSection
          label={tTradeNames('basic_information')}
          description={tTradeNames('basic_information_description')}
        >
          <FormGrid>
            {/* the type is picked before arriving on this screen; keep the select only when no
                type was provided so the form can still be completed. Changing it navigates to
                update the query param (see the form's type change listener). */}
            {!type && (
              <form.AppField name="type" mode="value">
                {field => (
                  <field.Select
                    size="sm"
                    label={t('type')}
                    placeholder={t('eg', {value: catalogTypeSelectItems?.[0]?.label})}
                    items={catalogTypeSelectItems}
                    required
                  />
                )}
              </form.AppField>
            )}

            {isActiveSubstance && <ActiveSubstanceSelect form={form} />}
            {isSupportiveMedication && <SupportiveMedicationSelect form={form} />}
            {isSolution && <SolutionSelect form={form} />}

            <HStack alignItems="flex-end" gapX="2">
              <Box flex="1" minWidth="0">
                <form.AppField
                  name="medicationProfileId"
                  mode="value"
                  listeners={{
                    onChange({value}) {
                      const profile = drug?.medicationProfiles?.find(p => p.id === value)
                      if (!profile) return
                      form.setFieldValue('isDrySubstance', Boolean(profile.isDrySubstance))

                      if (profile?.reconstitutionRequired) {
                        form.setFieldValue('reconstitutionRequired', true)
                      }
                    },
                  }}
                >
                  {field => (
                    <Tooltip content={drug ? null : tTradeNames('you_need_to_select_a_drug')}>
                      <field.Select
                        size="sm"
                        label={
                          <>
                            <Tooltip content={t('create_new_medicine_form_for_the_selected_drug')}>
                              <IconButton
                                size="sm"
                                variant="ghost"
                                height="max-content"
                                minWidth="max-content"
                                colorPalette="cyan"
                                disabled={!drug}
                                onClick={() => onAddMedicationProfile()}
                                aria-label={t('create_new_medicine_form_for_the_selected_drug')}
                              >
                                <LuPlus />
                              </IconButton>
                            </Tooltip>
                            {t('medicine_form')}
                          </>
                        }
                        placeholder={t('eg', {value: medicationProfileSelectItems?.[0]?.label})}
                        items={medicationProfileSelectItems}
                        required
                        disabled={!drug}
                      />
                    </Tooltip>
                  )}
                </form.AppField>
              </Box>
            </HStack>

            <form.AppField name="name">
              {field => (
                <field.TextField
                  size="sm"
                  label={t('product_name')}
                  placeholder={tTradeNames('name_placeholder')}
                  required
                />
              )}
            </form.AppField>

            <form.AppField name="prescribedName" mode="value">
              {field => (
                <field.CreatableCombobox
                  size="sm"
                  label={
                    <HStack>
                      {t('prescribed_name')}
                      <Tooltip content={tTradeNames('prescribed_name_tooltip')} />
                    </HStack>
                  }
                  items={prescribedNameItems}
                  placeholder={t('eg', {value: prescribedNameItems?.[0]?.label})}
                  required
                />
              )}
            </form.AppField>
          </FormGrid>

          <form.AppField name="isTrial">
            {field => <field.CheckBox size="sm" label={t('trial_product')} />}
          </form.AppField>

          {isActiveSubstance && (
            <form.AppField name="additionalApprovalRequired">
              {field => (
                <field.CheckBox
                  size="sm"
                  label={t('requires_additional_approval')}
                  description={t('requires_additional_approval_desc')}
                />
              )}
            </form.AppField>
          )}

          {isActiveSubstance && (
            <form.AppField name="containsAlcohol">
              {field => (
                <field.CheckBox
                  size="sm"
                  label={t('contains_alcohol')}
                  description={t('contains_alcohol_description')}
                />
              )}
            </form.AppField>
          )}
        </FormSection>

        <FormSection
          label={tTradeNames('clinical_properties_containers')}
          description={tTradeNames('clinical_properties_containers_description')}
        >
          <FormGrid>
            {(isActiveSubstance || isSupportiveMedication) && (
              <form.AppField name="strengthValue">
                {field => (
                  <field.NumberInput
                    size="sm"
                    label={t('strength')}
                    placeholder={tTradeNames('strength_value_placeholder')}
                    min={0}
                    required
                    hideControls
                    inputGroupProps={{
                      endElement: (
                        <form.AppField name="strengthUnitId" mode="value">
                          {field => (
                            <field.Select
                              label={t('strength_unit')}
                              placeholder={t('eg', {value: strengthUnitsSelectItems?.[0]?.label})}
                              items={strengthUnitsSelectItems}
                              hideLabel
                              required
                              variant="ghost"
                              size="xs"
                              controlProps={{minWidth: '20'}}
                              hideErrorMessage
                            />
                          )}
                        </form.AppField>
                      ),
                    }}
                  />
                )}
              </form.AppField>
            )}

            {!isDrySubstance && (
              <>
                <form.AppField name="volumeValue">
                  {field => (
                    <field.NumberInput
                      size="sm"
                      label={t('volume_value')}
                      placeholder={tTradeNames('volume_value_placeholder')}
                      min={0}
                      required
                      hideControls
                      inputGroupProps={{endElement: volumeUnitEndElement}}
                    />
                  )}
                </form.AppField>

                <form.AppField name="overfilledBy">
                  {field => (
                    <field.NumberInput
                      size="sm"
                      label={t('overfilled_by')}
                      placeholder={tTradeNames('volume_value_placeholder')}
                      min={0}
                      inputGroupProps={{endElement: volumeUnitEndElement}}
                    />
                  )}
                </form.AppField>

                <form.AppField name="actualVolume">
                  {field => (
                    <field.NumberInput
                      size="sm"
                      label={t('actual_volume')}
                      placeholder={tTradeNames('volume_value_placeholder')}
                      min={0}
                      disabled
                      inputGroupProps={{endElement: volumeUnitEndElement}}
                    />
                  )}
                </form.AppField>
              </>
            )}

            {!isSupportiveMedication && (
              <form.AppField name="density">
                {field => (
                  <field.NumberInput
                    size="sm"
                    label={t('density')}
                    placeholder={tTradeNames('density_placeholder')}
                    helpText={t('for_gravimetric_production')}
                    min={0}
                  />
                )}
              </form.AppField>
            )}

            {isSolution && (
              <>
                <form.AppField name="containerContents" mode="value">
                  {field => (
                    <field.Select
                      size="sm"
                      label={t('container_contents')}
                      placeholder={t('eg', {value: contentsSelectItems?.[0]?.label})}
                      items={contentsSelectItems}
                      required
                    />
                  )}
                </form.AppField>

                <form.AppField name="concentrationValue">
                  {field => (
                    <field.NumberInput
                      size="sm"
                      label={t('concentration')}
                      placeholder={tTradeNames('concentration_value_placeholder')}
                      min={0}
                      required
                      hideControls
                      errorMessage={concentrationUnitError}
                      inputGroupProps={{
                        endElement: (
                          <form.AppField name="concentrationUnitId" mode="value">
                            {field => (
                              <field.Select
                                label={t('concentration_unit')}
                                placeholder={t('eg', {value: concentrationUnitsSelectItems?.[0]?.label})}
                                items={concentrationUnitsSelectItems}
                                hideLabel
                                required
                                variant="ghost"
                                size="xs"
                                controlProps={{minWidth: '20'}}
                                hideErrorMessage
                              />
                            )}
                          </form.AppField>
                        ),
                      }}
                    />
                  )}
                </form.AppField>
              </>
            )}

            {!isDrySubstance && (
              <form.AppField name="maxVolumeCapacity">
                {field => (
                  <field.NumberInput
                    size="sm"
                    label={t('max_volume_capacity')}
                    placeholder={tTradeNames('max_volume_capacity_placeholder')}
                    min={0}
                    required={isSolution}
                    inputGroupProps={{endElement: volumeUnitEndElement}}
                  />
                )}
              </form.AppField>
            )}
          </FormGrid>

          {isActiveSubstance && (
            <>
              <form.AppField name="additionalsUsed">
                {field => <field.CheckBox size="sm" label={t('additionals_used')} />}
              </form.AppField>

              {additionalsUsed && (
                <FormGrid>
                  <Box>
                    <form.AppField name="additionalsDetails">
                      {field => (
                        <field.Textarea
                          size="sm"
                          label={t('additionals_details')}
                          placeholder={tTradeNames('additionals_details_placeholder')}
                          rows={2}
                        />
                      )}
                    </form.AppField>
                  </Box>

                  <form.AppField name="additionalsWeight">
                    {field => (
                      <field.NumberInput
                        size="sm"
                        label={t('additionals_weight')}
                        placeholder={tTradeNames('additionals_weight_placeholder')}
                        min={0}
                      />
                    )}
                  </form.AppField>
                </FormGrid>
              )}
            </>
          )}
        </FormSection>
      </Box>
    )
  },
})

const ActiveSubstanceSelect = withForm({
  defaultValues: getTradeNameFormDefaultValues(undefined),
  render: function Render({form}) {
    const {t} = useTranslate('common')
    const infiniteSubstances = useGetInfiniteActiveSubstanceDrugs()

    return (
      <form.AppField name="drugCatalogItemId" mode="value">
        {field => (
          <field.InfiniteCombobox
            size="sm"
            label={
              <>
                <IconButton
                  asChild
                  size="sm"
                  variant="ghost"
                  height="max-content"
                  minWidth="max-content"
                  colorPalette="cyan"
                >
                  <Link
                    to="/configuration/drug-catalog/active-substances/new"
                    state={{
                      navigate: {to: '/configuration/products/trade-names/new', search: {type: 'ACTIVE_SUBSTANCE'}},
                    }}
                  >
                    <LuPlus />
                  </Link>
                </IconButton>
                {t('active_substance')}
              </>
            }
            placeholder={t('eg', {value: t('substances')})}
            portalled={false}
            required
            {...infiniteSubstances}
          />
        )}
      </form.AppField>
    )
  },
})

const SupportiveMedicationSelect = withForm({
  defaultValues: getTradeNameFormDefaultValues(undefined),
  render: function Render({form}) {
    const {t} = useTranslate('common')
    const infiniteSupportiveMedications = useGetInfiniteSupportiveMedicationDrugs()

    return (
      <form.AppField name="drugCatalogItemId" mode="value">
        {field => (
          <field.InfiniteCombobox
            size="sm"
            label={
              <>
                <IconButton
                  asChild
                  size="sm"
                  variant="ghost"
                  height="max-content"
                  minWidth="max-content"
                  colorPalette="cyan"
                >
                  <Link
                    to="/configuration/drug-catalog/supportive-medications/new"
                    state={{
                      navigate: {
                        to: '/configuration/products/trade-names/new',
                        search: {type: 'SUPPORTIVE_MEDICATION'},
                      },
                    }}
                  >
                    <LuPlus />
                  </Link>
                </IconButton>
                {t('supportive_medication')}
              </>
            }
            placeholder={t('eg', {value: t('name')})}
            required
            {...infiniteSupportiveMedications}
          />
        )}
      </form.AppField>
    )
  },
})

const SolutionSelect = withForm({
  defaultValues: getTradeNameFormDefaultValues(undefined),
  render: function Render({form}) {
    const {t} = useTranslate('common')
    const infiniteSolutions = useGetInfiniteSolutionDrugs()

    return (
      <form.AppField name="drugCatalogItemId" mode="value">
        {field => (
          <field.InfiniteCombobox
            size="sm"
            label={
              <>
                <IconButton
                  asChild
                  size="sm"
                  variant="ghost"
                  height="max-content"
                  minWidth="max-content"
                  colorPalette="cyan"
                >
                  <Link
                    to="/configuration/drug-catalog/solutions/new"
                    state={{
                      navigate: {to: '/configuration/products/trade-names/new', search: {type: 'SOLUTION'}},
                    }}
                  >
                    <LuPlus />
                  </Link>
                </IconButton>
                {t('solution')}
              </>
            }
            placeholder={t('eg', {value: t('name')})}
            required
            {...infiniteSolutions}
          />
        )}
      </form.AppField>
    )
  },
})
